import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { roleLabel, fmtDate } from "@/lib/utils-format";
import { Plus, Trash2, ShieldCheck, RotateCcw, KeyRound, Pencil, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const empty = { name: "", email: "", password: "", role: "sales_executive", phone: "" };

const MODULE_LABELS = {
  dashboard: "Dashboard", leads: "Leads", customers: "Customers", sales: "Sales",
  purchase_orders: "Purchase Orders", products: "Products", ingredients: "Ingredients",
  recipes: "Recipes", qc: "Lab & QC", followups: "Follow-ups", tasks: "Tasks",
  notifications: "Notifications", hr: "HR", accounts: "Accounts",
  reports: "Reports", users: "Users", roles: "Roles",
  settings: "Settings", mobile: "Mobile (Field)",
};

// state: -1 = revoked, 0 = inherit (from role), 1 = granted
function permState(rolePerms, grants, revokes, mod, act) {
  const inRole = (rolePerms[mod] || []).includes(act);
  const inGrant = (grants[mod] || []).includes(act);
  const inRevoke = (revokes[mod] || []).includes(act);
  if (inRevoke) return -1;
  if (inGrant && !inRole) return 1;
  if (inRole && !inRevoke) return 0; // inherited
  return 0; // not present anywhere = inherit=off
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  // Permission override dialog state
  const [permOpen, setPermOpen] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [permMatrix, setPermMatrix] = useState(null); // {role, grants, revokes}
  const [permSaving, setPermSaving] = useState(false);

  // Manager PIN dialog state (super_admin only)
  const [pinOpen, setPinOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinStatus, setPinStatus] = useState(null);
  const [pinValue, setPinValue] = useState("");
  const [pinBusy, setPinBusy] = useState(false);

  // Edit user dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null); // { id, name, role, branch_id, phone }
  const [editBusy, setEditBusy] = useState(false);
  const [branches, setBranches] = useState([]);

  // Reset password dialog state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const load = async () => {
    const [u, r, b] = await Promise.all([
      api.get("/users"),
      api.get("/roles"),
      api.get("/branches").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setRoles(r.data.roles || []);
    setModules(r.data.modules || []);
    setActions(r.data.actions || []);
    setBranches(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email & password required");
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  const changeRole = async (u, role) => {
    await api.patch(`/users/${u.id}`, { role });
    toast.success("Role updated");
    load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // ---- Edit user (Name, Role, Factory/Branch ID, Phone). NEVER shows password. ----
  const openEdit = (u) => {
    setEditForm({
      id: u.id,
      name: u.name || "",
      role: u.role_id || u.role || "",
      branch_id: u.branch_id || "",
      phone: u.phone || "",
      email: u.email || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editForm?.name?.trim()) return toast.error("Name is required");
    setEditBusy(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        role: editForm.role, role_id: editForm.role,
        branch_id: editForm.branch_id || null,
        phone: editForm.phone || null,
      };
      await api.patch(`/users/${editForm.id}`, payload);
      toast.success("User updated");
      setEditOpen(false);
      setEditForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setEditBusy(false); }
  };

  // ---- Reset password ----
  const openPasswordReset = (u) => {
    setPwTarget(u); setPwValue(""); setPwConfirm(""); setPwShow(false);
    setPwOpen(true);
  };
  const savePassword = async () => {
    if (pwValue.length < 8) return toast.error("Password must be at least 8 characters");
    if (pwValue !== pwConfirm) return toast.error("Passwords don't match");
    setPwBusy(true);
    try {
      await api.patch(`/users/${pwTarget.id}`, { password: pwValue });
      toast.success(`Password reset for ${pwTarget.name}`);
      setPwOpen(false);
      setPwTarget(null); setPwValue(""); setPwConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally { setPwBusy(false); }
  };

  const openPermissions = async (u) => {
    setPermTarget(u);
    setPermOpen(true);
    setPermMatrix(null);
    try {
      const { data } = await api.get(`/users/${u.id}/permissions`);
      setPermMatrix({
        role: data.role_permissions || {},
        grants: data.permissions_grant || {},
        revokes: data.permissions_revoke || {},
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load permissions");
    }
  };

  const cyclePerm = (mod, act) => {
    if (!permMatrix) return;
    setPermMatrix((m) => {
      const grants = JSON.parse(JSON.stringify(m.grants || {}));
      const revokes = JSON.parse(JSON.stringify(m.revokes || {}));
      const inRole = (m.role[mod] || []).includes(act);
      const state = permState(m.role, grants, revokes, mod, act);
      // Remove from both first
      grants[mod] = (grants[mod] || []).filter((a) => a !== act);
      revokes[mod] = (revokes[mod] || []).filter((a) => a !== act);
      if (!grants[mod]?.length) delete grants[mod];
      if (!revokes[mod]?.length) delete revokes[mod];
      let next;
      if (inRole) {
        // Cycle: inherit(0) -> revoke(-1) -> inherit(0)
        next = state === -1 ? 0 : -1;
      } else {
        // Cycle: inherit-off(0) -> grant(+1) -> inherit-off(0)
        next = state === 1 ? 0 : 1;
      }
      if (next === 1) {
        grants[mod] = [...(grants[mod] || []), act];
      } else if (next === -1) {
        revokes[mod] = [...(revokes[mod] || []), act];
      }
      return { ...m, grants, revokes };
    });
  };

  const resetOverrides = () => {
    setPermMatrix((m) => ({ ...m, grants: {}, revokes: {} }));
  };

  const savePermissions = async () => {
    if (!permTarget || !permMatrix) return;
    setPermSaving(true);
    try {
      await api.put(`/users/${permTarget.id}/permissions`, {
        permissions_grant: permMatrix.grants || {},
        permissions_revoke: permMatrix.revokes || {},
      });
      toast.success("Permissions saved");
      setPermOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setPermSaving(false);
    }
  };

  // ---- Manager PIN handlers (super_admin only) ----
  const PIN_ELIGIBLE_ROLES = ["branch_manager", "factory_admin", "factory_manager", "store_manager", "super_admin", "admin"];
  const isSuper = me?.role === "super_admin" || me?.role_id === "super_admin";

  const openPinDialog = async (u) => {
    setPinTarget(u);
    setPinOpen(true);
    setPinStatus(null);
    setPinValue("");
    try {
      const { data } = await api.get(`/users/${u.id}/manager-pin`);
      setPinStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PIN status");
    }
  };

  const savePin = async () => {
    if (!/^\d{6}$/.test(pinValue)) { toast.error("PIN must be exactly 6 digits"); return; }
    setPinBusy(true);
    try {
      await api.put(`/users/${pinTarget.id}/manager-pin`, { pin: pinValue });
      toast.success("PIN saved");
      const { data } = await api.get(`/users/${pinTarget.id}/manager-pin`);
      setPinStatus(data);
      setPinValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save PIN");
    } finally { setPinBusy(false); }
  };

  const clearPin = async () => {
    if (!window.confirm(`Clear the manager PIN for ${pinTarget?.name}?`)) return;
    setPinBusy(true);
    try {
      await api.delete(`/users/${pinTarget.id}/manager-pin`);
      toast.success("PIN cleared");
      setPinStatus((s) => ({ ...(s || {}), has_pin: false, pin_set_at: null }));
      setPinValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear PIN");
    } finally { setPinBusy(false); }
  };

  const overrideCount = useMemo(() => {
    if (!permMatrix) return 0;
    const g = Object.values(permMatrix.grants || {}).reduce((s, v) => s + v.length, 0);
    const r = Object.values(permMatrix.revokes || {}).reduce((s, v) => s + v.length, 0);
    return g + r;
  }, [permMatrix]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Team</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Users & Roles</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="user-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">New User</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Name *</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email *</Label><Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Password *</Label><Input type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.is_system ? "" : " (custom)"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="user-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Overrides</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const overrideN = Object.values(u.permissions_grant || {}).reduce((s, v) => s + (v?.length || 0), 0)
                + Object.values(u.permissions_revoke || {}).reduce((s, v) => s + (v?.length || 0), 0);
              return (
                <tr key={u.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-[#5C4033]">{u.email}</td>
                  <td className="px-4 py-3">
                    {me.id === u.id ? (
                      <Badge variant="outline" className="capitalize">{(roles.find(r => r.id === u.role)?.name) || roleLabel(u.role)}</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="h-8 w-40" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {me?.role === "super_admin" || me?.role_id === "super_admin" ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => openPermissions(u)}
                          data-testid={`user-perms-${u.id}`}
                          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-[#EAE5D9] hover:border-[#C05621] hover:text-[#C05621]"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                          {overrideN > 0 ? `${overrideN} override${overrideN === 1 ? "" : "s"}` : "Configure"}
                        </button>
                        {PIN_ELIGIBLE_ROLES.includes(u.role) && (
                          <button
                            onClick={() => openPinDialog(u)}
                            data-testid={`user-pin-${u.id}`}
                            className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border ${
                              u.manager_pin_hash || u.has_pin
                                ? "border-emerald-300 text-emerald-700 bg-emerald-50 hover:border-emerald-500"
                                : "border-[#EAE5D9] hover:border-[#C05621] hover:text-[#C05621]"
                            }`}
                            title="Manage 6-digit POS discount approval PIN"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                            {u.manager_pin_hash || u.has_pin ? "PIN set" : "Set PIN"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-[#8B7355]">{overrideN}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#5C4033]">{u.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)} disabled={me.id === u.id} />
                  </td>
                  <td className="px-4 py-3 text-[#8B7355]">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`user-edit-${u.id}`}
                        title="Edit user"
                        className="text-slate-500 hover:text-[#C05621] p-1 rounded hover:bg-[#FEEBC8]"
                      ><Pencil className="w-4 h-4" /></button>
                      <button
                        onClick={() => openPasswordReset(u)}
                        data-testid={`user-reset-pw-${u.id}`}
                        title="Reset password"
                        className="text-slate-500 hover:text-[#C05621] p-1 rounded hover:bg-[#FEEBC8]"
                      ><Lock className="w-4 h-4" /></button>
                      {me.id !== u.id && (
                        <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per-user permission override matrix */}
      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#C05621]" />
              Per-user permissions
              {permTarget && (
                <span className="text-sm font-normal text-[#8B7355]">— {permTarget.name} <span className="text-xs">({permTarget.email})</span></span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8B7355]">
              Grant extra permissions or revoke inherited ones on top of this user's assigned role. Effective = role + grants − revokes.
            </DialogDescription>
          </DialogHeader>

          {!permMatrix ? (
            <div className="py-8 text-center text-[#8B7355] text-sm">Loading permissions…</div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="bg-[#FEF9EA] border border-amber-200 rounded-md p-3 text-xs text-[#5C4033]">
                <div className="font-semibold mb-1">Click a cell to cycle its state:</div>
                <div className="flex flex-wrap gap-3 items-center">
                  <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded border-2 border-emerald-500 bg-emerald-500 text-white text-[10px] flex items-center justify-center">✓</span> from role</span>
                  <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded border-2 border-[#C05621] bg-[#FEEBC8] text-[#C05621] text-[10px] flex items-center justify-center font-bold">+</span> grant extra</span>
                  <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded border-2 border-red-500 bg-red-50 text-red-600 text-[10px] flex items-center justify-center font-bold">✕</span> revoke</span>
                  <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded border border-[#EAE5D9] bg-white"></span> not granted</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-[#8B7355]">
                  <span className="font-semibold text-[#2C1810]">{overrideCount}</span> override{overrideCount === 1 ? "" : "s"} applied on top of role permissions
                </div>
                <button
                  onClick={resetOverrides}
                  data-testid="perm-reset"
                  className="text-xs text-[#C05621] hover:underline flex items-center gap-1"
                >
                  <RotateCcw className="w-3 h-3" /> Reset all overrides
                </button>
              </div>

              <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F2E8] uc-label sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Module</th>
                      {actions.map((a) => <th key={a} className="text-center px-2 py-2 capitalize w-20">{a}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((m) => (
                      <tr key={m} className="border-t border-[#EAE5D9]">
                        <td className="px-3 py-2 font-medium">{MODULE_LABELS[m] || m}</td>
                        {actions.map((a) => {
                          const state = permState(permMatrix.role, permMatrix.grants, permMatrix.revokes, m, a);
                          const inRole = (permMatrix.role[m] || []).includes(a);
                          let cls = "border-[#EAE5D9] bg-white text-transparent"; let label = "";
                          if (state === 0 && inRole) { cls = "border-emerald-500 bg-emerald-500 text-white"; label = "✓"; }
                          else if (state === 1) { cls = "border-[#C05621] bg-[#FEEBC8] text-[#C05621] font-bold"; label = "+"; }
                          else if (state === -1) { cls = "border-red-500 bg-red-50 text-red-600 font-bold"; label = "✕"; }
                          return (
                            <td key={a} className="text-center px-2 py-2">
                              <button
                                type="button"
                                onClick={() => cyclePerm(m, a)}
                                data-testid={`perm-${m}-${a}`}
                                className={`w-6 h-6 rounded border-2 flex items-center justify-center text-xs mx-auto ${cls}`}
                                title={`${m}:${a} — ${state === -1 ? "revoked" : state === 1 ? "granted" : inRole ? "from role" : "not granted"}`}
                              >
                                {label}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-[#8B7355]">
                Effective permissions = role permissions <span className="text-[#C05621] font-semibold">+ your grants</span> <span className="text-red-600 font-semibold">− your revokes</span>. Super Admin always has full access.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermOpen(false)}>Cancel</Button>
            <Button
              onClick={savePermissions}
              disabled={permSaving || !permMatrix}
              data-testid="perm-save-btn"
              className="bg-[#C05621] hover:bg-[#9C4221] text-white"
            >
              {permSaving ? "Saving…" : "Save overrides"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manager PIN dialog (super_admin only) */}
      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="user-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C05621]" />
              Manager PIN
              {pinTarget && (
                <span className="text-sm font-normal text-[#8B7355]">
                  — {pinTarget.name} <span className="text-xs">({pinTarget.email})</span>
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8B7355]">
              A 6-digit numeric PIN this manager can use at the POS to approve
              discounts without typing their email and password. Only Super Admin
              can set or reset PINs.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="bg-[#FDFBF7] border border-[#EAE5D9] rounded-md p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-[#8B7355]">Current PIN status</span>
                {pinStatus?.has_pin ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Active</Badge>
                ) : (
                  <Badge variant="outline" className="text-[#8B7355]">Not set</Badge>
                )}
              </div>
              {pinStatus?.pin_set_at && (
                <div className="text-[11px] text-[#8B7355] mt-1">
                  Set: {fmtDate(pinStatus.pin_set_at)}
                </div>
              )}
            </div>

            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">New 6-digit PIN</Label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••"
                autoComplete="one-time-code"
                data-testid="user-pin-input"
                className="tracking-[0.5em] text-center text-lg font-mono h-11"
              />
              <div className="text-[11px] text-[#8B7355] mt-1">
                Digits only (0-9). Must be unique within the branch.
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            {pinStatus?.has_pin && (
              <Button
                type="button"
                variant="outline"
                onClick={clearPin}
                disabled={pinBusy}
                data-testid="user-pin-clear-btn"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                Clear PIN
              </Button>
            )}
            <Button variant="outline" onClick={() => setPinOpen(false)}>Cancel</Button>
            <Button
              onClick={savePin}
              disabled={pinBusy || pinValue.length !== 6}
              data-testid="user-pin-save-btn"
              className="bg-[#C05621] hover:bg-[#9C4221] text-white"
            >
              {pinBusy ? "Saving…" : pinStatus?.has_pin ? "Reset PIN" : "Set PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog — Name, Role, Factory/Branch ID, Phone. Password is NEVER shown here. */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#C05621]" />
              Edit user
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8B7355]">
              Update this staff member's details. To change the password, use the <b>Reset Password</b> button on their row.
            </DialogDescription>
          </DialogHeader>

          {editForm && (
            <div className="space-y-3 py-2">
              <div>
                <Label>Email</Label>
                <Input value={editForm.email} disabled className="bg-slate-50" />
                <div className="text-[10px] text-[#8B7355] mt-1">Email cannot be changed. Delete the user and re-create if it must change.</div>
              </div>
              <div><Label>Name *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="user-edit-name" /></div>
              <div>
                <Label>Role *</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.is_system ? "" : " (custom)"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Factory / Branch</Label>
                <Select value={editForm.branch_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, branch_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="user-edit-branch"><SelectValue placeholder="— (global user)" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__none__">— None (global user) —</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name} <span className="text-[10px] uppercase text-[#8B7355] ml-1">{b.type || "branch"}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-[#8B7355] mt-1">Required for Factory Admin, Factory Manager and Showroom roles.</div>
              </div>
              <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="user-edit-phone" /></div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} data-testid="user-edit-save" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog — only shows "Enter new password", never reveals existing password */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="user-reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#C05621]" />
              Reset password
              {pwTarget && (
                <span className="text-sm font-normal text-[#8B7355]">— {pwTarget.name}</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8B7355]">
              Set a new password for <b className="text-[#5C4033]">{pwTarget?.email}</b>. For security, the current password is never displayed and cannot be recovered — only replaced.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label>New password *</Label>
              <div className="relative">
                <Input
                  type={pwShow ? "text" : "password"}
                  value={pwValue}
                  onChange={(e) => setPwValue(e.target.value)}
                  data-testid="user-reset-pw-new"
                  placeholder="At least 8 characters"
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setPwShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="user-reset-pw-toggle"
                  aria-label={pwShow ? "Hide password" : "Show password"}
                >
                  {pwShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirm new password *</Label>
              <Input
                type={pwShow ? "text" : "password"}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                data-testid="user-reset-pw-confirm"
                autoComplete="new-password"
              />
              {pwConfirm && pwConfirm !== pwValue && (
                <div className="text-xs text-red-600 mt-1">Passwords don't match</div>
              )}
            </div>
            <div className="bg-[#FEF9EA] border border-amber-200 rounded-md p-2 text-xs text-[#5C4033]">
              The user will be able to log in with this new password immediately. Share it with them through a secure channel.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button
              onClick={savePassword}
              disabled={pwBusy || pwValue.length < 8 || pwValue !== pwConfirm}
              data-testid="user-reset-pw-save"
              className="bg-[#C05621] hover:bg-[#9C4221] text-white"
            >
              {pwBusy ? "Saving…" : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
